import React from "react";
import { ActivityDashboardResponse } from "../../api/analyticsAdmin";
import { formatSessionDuration } from "../../helpers/analyticsPlus";
import { formatInteger } from "../../helpers/formatNumber";
import {
  deltaLabel,
  formatOptional,
  latestValue,
} from "../../helpers/dashboardMetrics";
import { StatTile } from "./StatTile";
import { MonthlyActivity } from "./useDashboardData";
import { activitySeries } from "./useMonthlyActivity";

interface ActivityHighlightsProps {
  activity: MonthlyActivity | null;
}

// Reads the last complete month, like every other figure on the page. It used to
// read a rolling 30 days, which put two different periods on one screen and,
// being computed from today in the browser, could never match a range warmed
// from today on the server.
const ActivityHighlights: React.FC<ActivityHighlightsProps> = ({ activity }) => {
  if (!activity)
    return (
      <div className="bg-white rounded-2xl shadow p-5 mb-8 text-sm text-gray-500">
        Activity figures are unavailable right now.
      </div>
    );

  const today = new Date();
  const seriesFor = (pick: (data: ActivityDashboardResponse) => number | null | undefined) =>
    activitySeries(activity, today, pick);

  const tiles = [
    {
      label: "Unique visitors",
      series: seriesFor((a) => a.activeUsers),
      format: formatInteger,
    },
    {
      label: "Sessions",
      series: seriesFor((a) => a.totalSessions),
      format: formatInteger,
    },
    {
      label: "Avg session",
      series: seriesFor((a) => a.avgSessionDurationSeconds),
      format: formatSessionDuration,
    },
    {
      label: "Stickiness (DAU/MAU)",
      series: seriesFor((a) => a.stickinessPercent),
      format: (value: number) => `${value.toFixed(1)}%`,
    },
  ];

  const caption = deltaLabel(tiles[0].series) ?? undefined;

  return (
    <section className="mb-8">
      <h2 className="text-sm font-medium text-gray-500 mb-3">App activity</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {tiles.map((tile) => (
          <StatTile
            key={tile.label}
            label={tile.label}
            value={formatOptional(latestValue(tile.series), tile.format)}
            series={tile.series}
            caption={caption}
          />
        ))}
      </div>
    </section>
  );
};

export default ActivityHighlights;
